import { useMemo, useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { usePrestadores } from '@/hooks/usePrestadores';
import { useProfilesComRoles } from '@/hooks/useProfiles';
import { useAgendaOS, useReagendarOSAvancado } from '@/hooks/useAgenda';
import { filtrarOSPorPrestador } from '@/lib/agenda-filtro';
import { filtrarPrestadoresAtribuiveis } from '@/lib/prestadores-atribuiveis';
import { getWeekRange, getDayRange, computeDropTime } from '@/lib/agenda-timeline';
import { resolveOSDuration } from '@/services/ordens-servico.service';
import { toast } from 'sonner';
import { AgendaToolbar } from './AgendaToolbar';
import { AgendaSidebar } from './AgendaSidebar';
import { type AgendaView } from './TimelineGrid';
import { WeekCalendarView } from './WeekCalendarView';
import { getPosthog } from '@/lib/analytics';

const UNASSIGNED_ROW_ID = '__unassigned__';

export function AgendaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get('view') === 'day' ? 'day' : 'week') as AgendaView;
  const viewDate = useMemo(() => {
    const raw = searchParams.get('date');
    return raw ? new Date(raw) : new Date();
  }, [searchParams]);

  const [prestadorSelecionadoId, setPrestadorSelecionadoId] = useState<string | null>(null);

  const range = useMemo(
    () => (view === 'week' ? getWeekRange(viewDate) : getDayRange(viewDate)),
    [view, viewDate],
  );

  const { data: ordens, isLoading: loadingOrdens } = useAgendaOS(range);
  const { data: prestadoresRaw, isLoading: loadingPrestadores } = usePrestadores();
  const { data: prestadoresAtivos } = useProfilesComRoles('prestador');
  const reagendar = useReagendarOSAvancado();

  // Só prestadores de membros ATIVOS (exclui desativados, que perderam acesso).
  const prestadores = useMemo(
    () =>
      filtrarPrestadoresAtribuiveis(
        prestadoresRaw ?? [],
        new Set((prestadoresAtivos ?? []).map((p) => p.id)),
      ).map((p) => ({
        id: p.id,
        nome: p.profile?.nome ?? '—',
        avatarUrl: p.profile?.avatar_url ?? null,
      })),
    [prestadoresRaw, prestadoresAtivos],
  );

  useEffect(() => {
    const ph = getPosthog();
    if (ph) ph.capture('agenda_viewed', { view, has_filters: prestadorSelecionadoId !== null });
  }, [view, prestadorSelecionadoId]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const activeData = active.data.current as
        | { type: string; osId: string; status?: string }
        | undefined;
      const overData = over.data.current as
        | { type: string; rowId?: string; day?: string }
        | undefined;
      if (!activeData || !overData) return;

      const osId = activeData.osId;

      // Week calendar view: drop on a day column changes only the time.
      // Prestador stays the same (the week view doesn't show prestador rows).
      if (overData.type === 'slot-week' && overData.day) {
        const day = new Date(overData.day);
        const overRect = over.rect;
        const activeRect = active.rect.current.translated ?? active.rect.current.initial;
        const fraction =
          activeRect && overRect.height > 0
            ? (activeRect.top + activeRect.height / 2 - overRect.top) / overRect.height
            : 0;
        const snapped = computeDropTime(day, fraction);
        const draggedOS = (ordens ?? []).find((os) => os.id === osId);
        const prestadorId = draggedOS?.prestador_id ?? null;
        reagendar.mutate({
          osId,
          dataAgendada: snapped,
          prestadorId,
          motivo: 'drag-and-drop (week calendar)',
        });
        const source = 'timeline';
        const ph = getPosthog();
        if (ph)
          ph.capture('agenda_os_rescheduled', {
            source,
            changed_prestador: false,
            changed_time: true,
            os_status: activeData.status ?? '',
          });
        return;
      }

      if (overData.type === 'slot' && overData.day && overData.rowId) {
        const day = new Date(overData.day);
        const overRect = over.rect;
        const activeRect = active.rect.current.translated ?? active.rect.current.initial;
        const fraction =
          activeRect && overRect.width > 0
            ? (activeRect.left + activeRect.width / 2 - overRect.left) / overRect.width
            : 0;
        const snapped = computeDropTime(day, fraction);
        const prestadorId = overData.rowId === UNASSIGNED_ROW_ID ? null : overData.rowId;

        const draggedOS = (ordens ?? []).find((os) => os.id === osId);
        const draggedDuration = draggedOS
          ? resolveOSDuration({
              duracao_estimada_minutos: draggedOS.duracao_estimada_minutos,
              itens: draggedOS.itens,
            })
          : 60;
        const newStart = snapped.getTime();
        const newEnd = newStart + draggedDuration * 60000;
        const conflict = (ordens ?? []).find((other) => {
          if (other.id === osId) return false;
          if (other.prestador_id !== prestadorId) return false;
          if (!other.data_agendada) return false;
          const otherDuration = resolveOSDuration({
            duracao_estimada_minutos: other.duracao_estimada_minutos,
            itens: other.itens,
          });
          const otherStart = new Date(other.data_agendada).getTime();
          const otherEnd = otherStart + otherDuration * 60000;
          return newStart < otherEnd && otherStart < newEnd;
        });
        if (conflict) {
          const fmt = (ms: number) => {
            const d = new Date(ms);
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
          };
          const confStart = new Date(conflict.data_agendada as string).getTime();
          const confDuration = resolveOSDuration({
            duracao_estimada_minutos: conflict.duracao_estimada_minutos,
            itens: conflict.itens,
          });
          toast.warning(
            `OS agendada. Atenção: sobrepõe com OS ${conflict.numero_os} de ${fmt(confStart)}–${fmt(confStart + confDuration * 60000)}.`,
          );
        }

        reagendar.mutate({
          osId,
          dataAgendada: snapped,
          prestadorId,
          motivo: 'drag-and-drop',
        });

        const source = 'timeline';
        const ph = getPosthog();
        if (ph)
          ph.capture('agenda_os_rescheduled', {
            source,
            changed_prestador: true,
            changed_time: true,
            os_status: activeData.status ?? '',
          });
      }
    },
    [reagendar, ordens],
  );

  function setView(v: AgendaView) {
    const next = new URLSearchParams(searchParams);
    next.set('view', v);
    setSearchParams(next);
  }
  function setDate(d: Date) {
    const next = new URLSearchParams(searchParams);
    next.set('date', d.toISOString());
    setSearchParams(next);
  }

  return (
    <MainLayout>
      <div className="flex h-full flex-col p-6 gap-4">
        <PageHeader
          titulo={t('agenda.title')}
          acoes={
            <>
              <UserMenu />
            </>
          }
        />

        <AgendaToolbar
          view={view}
          viewDate={viewDate}
          onViewChange={setView}
          onDateChange={setDate}
        />

        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="flex flex-1 gap-4 overflow-hidden">
            {/* Coluna de prestadores: clicar filtra o calendário */}
            <div className="flex flex-shrink-0 overflow-hidden rounded-2xl border border-border-soft bg-card shadow-sm">
              <AgendaSidebar
                prestadores={prestadores}
                selecionadoId={prestadorSelecionadoId}
                onSelecionar={setPrestadorSelecionadoId}
                isLoading={loadingPrestadores}
              />
            </div>

            {/* Calendário (vista de semana ou dia) como card próprio. Ambas
                usam o mesmo layout vertical (horas Y, dias X) — em vista de
                dia a grelha tem apenas 1 coluna. */}
            <div className="flex flex-1 overflow-hidden rounded-2xl border border-border-soft bg-card shadow-sm">
              {loadingOrdens ? (
                <div className="flex-1 animate-pulse bg-muted/20" />
              ) : (
                <WeekCalendarView
                  view={view}
                  viewDate={viewDate}
                  prestadores={prestadores}
                  ordens={filtrarOSPorPrestador(ordens ?? [], prestadorSelecionadoId)}
                  onOSClick={(osId) => navigate(`/ordens/${osId}`)}
                />
              )}
            </div>
          </div>
        </DndContext>
      </div>
    </MainLayout>
  );
}
